import React from 'react';
import PropTypes from 'prop-types';
import { BarChart, XAxis, YAxis, Tooltip, Legend, Bar, ResponsiveContainer, CartesianGrid } from 'recharts'; // eslint-disable-line import/no-unresolved
import Radium from 'radium';

import { appConfig } from '../../../config/customization';
import CustomXAxisLabel from '../UTChart/components/CustomXAxisLabel';
import CustomYAxisLabel from '../UTChart/components/CustomYAxisLabel';

import UTTooltip from './components/UTTooltip';
import CustomBar from './components/CustomBar';
import styles from './styles';

const firstColor = [
  appConfig.colors.historyFirstColorDark,
  appConfig.colors.historyFirstColorNormal,
  appConfig.colors.historyFirstColorSoft
];

const secondColor = [
  appConfig.colors.historySecondColorDark,
  appConfig.colors.historySecondColorNormal,
  appConfig.colors.historySecondColorSoft
];

const thirdColor = [
  appConfig.colors.historyThirdColorDark,
  appConfig.colors.historyThirdColorNormal,
  appConfig.colors.historyThirdColorSoft
];

const createBar = (name, dataKey, colorVariant, years, handleClick) => (
  <Bar
    style={styles.baseStyle}
    name={name}
    shape={
      <CustomBar
        fill={[
          appConfig.colors[`historyFirstColor${colorVariant}`],
          appConfig.colors[`historySecondColor${colorVariant}`],
          appConfig.colors[`historyThirdColor${colorVariant}`]
        ]}
        years={years}
        onClick={handleClick}
      />
    }
    maxBarSize={15}
    animationEasing="ease-out"
    dataKey={dataKey}
  />
);

const retrieveLegend = years => {
  const legend = [];
  if (years[2]) legend.push({ value: 'Ant.', color: appConfig.colors.historyThirdColorDark });
  if (years[1]) legend.push({ value: years[1], color: appConfig.colors.historySecondColorDark });
  if (years[0]) legend.push({ value: years[0], color: appConfig.colors.historyFirstColorDark });
  return legend;
};

const isIE = navigator.userAgent.indexOf('MSIE') !== -1 || !!document.documentMode === true;

function UTBarChart({ data, dataKey, unit, currencyUnit, keys, activeValue, years, handleClick }) {
  const formatTooltip = value => `${value} ${unit}`;

  return (
    <div style={[styles.container, isIE && { overflow: 'hidden' }]}>
      <ResponsiveContainer minHeight={270} maxHeight={500}>
        <BarChart data={data} margin={styles.chart}>
          <XAxis
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={
              <CustomXAxisLabel
                activeValue={activeValue}
                activeFirstColor={appConfig.colors.historyFirstColorDark}
                activeSecondColor={appConfig.colors.historySecondColorDark}
                activeThirdColor={appConfig.colors.historyThirdColorDark}
                inactiveColor={appConfig.colors.gray}
                years={years}
              />
            }
            tickCount={14}
            dataKey={dataKey}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickCount={6}
            tick={<CustomYAxisLabel unit={unit} currencyUnit={currencyUnit} color={appConfig.colors.gray} />}
          />
          <CartesianGrid vertical={false} />
          <Legend
            verticalAlign="top"
            align="right"
            height={50}
            payload={retrieveLegend(years)}
            iconType="circle"
            iconSize={10}
            wrapperStyle={styles.legend}
          />
          {keys.firstKey && createBar(keys.firstName, keys.firstKey, 'Dark', years, handleClick)}
          {keys.secondKey && createBar(keys.secondName, keys.secondKey, 'Normal', years, handleClick)}
          {keys.thirdKey && createBar(keys.thirdName, keys.thirdKey, 'Soft', years, handleClick)}
          <Tooltip
            cursor={false}
            isAnimationActive={false}
            formatter={formatTooltip}
            unit={unit}
            content={<UTTooltip firstColor={firstColor} secondColor={secondColor} thirdColor={thirdColor} />}
            wrapperStyle={styles.wrapper}
            labelStyle={styles.tooltipText}
            itemStyle={styles.tooltipText}
            years={years}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

UTBarChart.propTypes = {
  data: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  dataKey: PropTypes.string.isRequired,
  unit: PropTypes.string,
  currencyUnit: PropTypes.bool,
  activeValue: PropTypes.string,
  keys: PropTypes.shape({
    firstKey: PropTypes.string,
    secondKey: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    thirdKey: PropTypes.string,
    firstName: PropTypes.string,
    secondName: PropTypes.string,
    thirdName: PropTypes.string
  }),
  years: PropTypes.arrayOf(PropTypes.number),
  handleClick: PropTypes.func.isRequired
};

export default Radium(UTBarChart);
